import request from '../../../util/SuperagentUtils';
import {
  adminBookingsReportEndpoints,
  buyerBookingsReportEndpoints,
  supplierBookingsReportEndpoints,
} from '../../../requests/api-endpoints';
import type { AssistantAudience } from '../assistantCapabilities';

/**
 * Looks a booking up by its order number, for the booking and payment-failure
 * lookups.
 *
 * The booking a user names ("booking 1021445") is the *order number*, not the
 * internal id the by-id endpoints take, so a lookup has to go through search.
 * Every audience's booking search accepts a `bookingNumber` filter and scopes
 * the rest to the caller's token — a buyer sees only their bookings, a supplier
 * only theirs — so the endpoint is chosen from the audience and nothing else is
 * sent. The admin search is account-wide, which is correct for staff.
 *
 * The search matches the number **partially** (SQL `LIKE '%n%'`), so a page can
 * hold other bookings whose numbers merely contain the one asked about — and the
 * first row need not be the right one. Callers must pick the exact match out of
 * the page; {@link BOOKING_MATCH_PAGE_SIZE} is sized so it is in it.
 */

/** Rows fetched to find one booking among partial matches of its number. */
export const BOOKING_MATCH_PAGE_SIZE = 10;

/** One booking row. Loose and local — only the fields the skill reads. */
export interface BookingLookupRow {
  orderNumber?: string;
  status?: string;
  locationName?: string;
  supplierCompanyName?: string;
  buyerCompanyName?: string;
  numberOfSpaces?: number;
  startDate?: string;
  endDate?: string;
  creationDate?: string;
  /** Cents; buyer and supplier rows only. */
  recurringCharge?: number;
  /** Cents on buyer and supplier rows; a formatted "$1,234.56" on staff rows. */
  initialCharge?: number | string;
  buyerEmail?: string;
  assetType?: string;
  durationType?: string;
  frequency?: string;
  bookingSubscriptionStatus?: string;
  chargeOverages?: boolean | string;
  /** An enum name on buyer and supplier rows; the display label on staff rows. */
  reasonDeclined?: string;
  reasonDeclinedNote?: string;
  paymentMethodDescription?: string;
  approvedBy?: string;
  approvedOn?: string;
  recurringSupplierPayoutAmount?: number;
  initialSupplierPayoutAmount?: number;
  buyerRatePerSpace?: string;
  overageRate?: string;
  brokered?: boolean | string;
  endDateAdjusted?: boolean | string;
}

const SEARCH_ENDPOINT: Record<AssistantAudience, string> = {
  buyer: buyerBookingsReportEndpoints.search,
  supplier: supplierBookingsReportEndpoints.search,
  admin: adminBookingsReportEndpoints.search,
};

export const searchBookingByNumber = (
  audience: AssistantAudience,
  bookingNumber: string,
): Promise<{ body?: { content?: BookingLookupRow[]; count?: number } }> => {
  const params = new URLSearchParams({
    bookingNumber,
    page: '0',
    size: String(BOOKING_MATCH_PAGE_SIZE),
  });
  return request.get(`${SEARCH_ENDPOINT[audience]}?${params.toString()}`);
};
